package com.swetdot.swetgram.data.api

import com.swetdot.swetgram.protocol.ClientHeaders
import com.swetdot.swetgram.protocol.ProtocolJson
import com.swetdot.swetgram.protocol.dto.ErrorResponse
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.HttpRequestTimeoutException
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.header
import io.ktor.client.request.request
import io.ktor.client.statement.HttpResponse
import io.ktor.client.statement.bodyAsText
import io.ktor.http.Headers
import io.ktor.http.HttpMethod
import io.ktor.http.isSuccess
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay

/**
 * Executes requests and turns every failure into an [ApiError].
 *
 * Retry policy is taken verbatim from DECISIONS §11:
 *   "GET requests may retry twice with exponential backoff."
 *   "Mutating requests retry only when explicitly idempotent."
 *
 * So a GET retries automatically, and a POST/PATCH/DELETE does not -- unless the caller opts in
 * by passing an idempotency key, which is the only thing that makes a repeat safe. Retrying a
 * plain POST would risk sending the same message twice.
 */
class ApiClient(
    private val http: HttpClient,
    private val tokenProvider: suspend () -> String?,
    private val onSessionExpired: suspend () -> Unit = {},
) {
    companion object {
        const val MAX_GET_ATTEMPTS = 3 // the initial attempt plus the two retries
        const val BACKOFF_BASE_MS = 400L
    }

    suspend inline fun <reified T> get(path: String, noinline block: HttpRequestBuilder.() -> Unit = {}): T =
        execute(HttpMethod.Get, path, idempotencyKey = null, block = block)

    /** Private ephemeral images use the same authentication and retry policy as JSON reads. */
    suspend fun getBytes(path: String): ByteArray =
        executeHttpResponse(HttpMethod.Get, path, null, true) {
            header("Accept", "image/jpeg")
            header("Cache-Control", "no-store")
        }.body()

    /**
     * [authenticated] MUST be false for the endpoints the contract marks `security: []`.
     * It is not an optimisation: attaching a token means asking the token provider for one,
     * and on /auth/refresh that re-enters the refresh lock and deadlocks the caller forever.
     */
    suspend inline fun <reified T> post(
        path: String,
        idempotencyKey: String? = null,
        authenticated: Boolean = true,
        noinline block: HttpRequestBuilder.() -> Unit = {},
    ): T = execute(HttpMethod.Post, path, idempotencyKey, authenticated, block)

    suspend inline fun <reified T> patch(path: String, noinline block: HttpRequestBuilder.() -> Unit = {}): T =
        execute(HttpMethod.Patch, path, idempotencyKey = null, block = block)

    suspend inline fun <reified T> put(path: String, noinline block: HttpRequestBuilder.() -> Unit = {}): T =
        execute(HttpMethod.Put, path, idempotencyKey = null, block = block)

    suspend inline fun <reified T> delete(path: String, noinline block: HttpRequestBuilder.() -> Unit = {}): T =
        execute(HttpMethod.Delete, path, idempotencyKey = null, block = block)

    suspend inline fun <reified T> execute(
        method: HttpMethod,
        path: String,
        idempotencyKey: String?,
        authenticated: Boolean = true,
        noinline block: HttpRequestBuilder.() -> Unit,
    ): T {
        val text = executeRaw(method, path, idempotencyKey, authenticated, block)
        if (T::class == Unit::class) return Unit as T
        return ProtocolJson.decodeFromString(text)
    }

    /**
     * Returns the raw body so the reified decode happens in the caller. Kept non-inline because
     * this is where the retry loop and error mapping live, and neither belongs in every call
     * site's generated code.
     */
    suspend fun executeRaw(
        method: HttpMethod,
        path: String,
        idempotencyKey: String?,
        authenticated: Boolean = true,
        block: HttpRequestBuilder.() -> Unit,
    ): String = executeRawResponse(method, path, idempotencyKey, authenticated, block).body

    /** A successful raw body together with response metadata needed by conditional reads. */
    data class RawResponse(
        val body: String,
        val headers: Headers,
    )

    /** Header-preserving [executeRaw], sharing the same auth, retry and error policy. */
    suspend fun executeRawResponse(
        method: HttpMethod,
        path: String,
        idempotencyKey: String?,
        authenticated: Boolean = true,
        block: HttpRequestBuilder.() -> Unit,
    ): RawResponse {
        val response = executeHttpResponse(method, path, idempotencyKey, authenticated, block)
        return RawResponse(body = response.bodyAsText(), headers = response.headers)
    }

    private suspend fun executeHttpResponse(
        method: HttpMethod,
        path: String,
        idempotencyKey: String?,
        authenticated: Boolean,
        block: HttpRequestBuilder.() -> Unit,
    ): HttpResponse {
        // A GET is safe to repeat by definition. Anything else is repeatable only when the
        // caller supplied an idempotency key, which is what lets the server collapse duplicates.
        val retryable = method == HttpMethod.Get || idempotencyKey != null
        val maxAttempts = if (retryable) MAX_GET_ATTEMPTS else 1

        var lastError: ApiError? = null

        repeat(maxAttempts) { attempt ->
            try {
                val response = http.request(path) {
                    this.method = method
                    if (authenticated) tokenProvider()?.let { header("Authorization", "Bearer $it") }
                    idempotencyKey?.let { header(ClientHeaders.IDEMPOTENCY_KEY, it) }
                    block()
                }
                if (response.status.isSuccess()) {
                    return response
                }

                val error = response.toApiError()
                // A suspension ends the session as thoroughly as a revocation does, and for the
                // same reason: the token cannot be made to work by refreshing it. Without this the
                // app held a session it could not use, failed every screen with a generic error,
                // and never returned to the one screen that can explain why.
                if (error is ApiError.SessionExpired || error is ApiError.AccountSuspended) onSessionExpired()
                // Only transport-ish failures are worth repeating; a 400 will fail identically.
                if (!error.isRetryable || attempt == maxAttempts - 1) throw error
                lastError = error
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (timeout: HttpRequestTimeoutException) {
                val error = ApiError.Timeout(timeout)
                if (attempt == maxAttempts - 1) throw error
                lastError = error
            } catch (api: ApiError) {
                throw api
            } catch (other: Throwable) {
                val error = ApiError.Offline(other)
                if (attempt == maxAttempts - 1) throw error
                lastError = error
            }

            // Exponential: 400ms, then 800ms. Deliberately short -- the request timeout is 15s,
            // and a user staring at a spinner will not wait through a long ladder.
            delay(BACKOFF_BASE_MS shl attempt)
        }

        throw lastError ?: ApiError.Offline(null)
    }

    /** Parses the contract's error envelope, falling back sensibly when the body is not one. */
    private suspend fun HttpResponse.toApiError(): ApiError {
        val status = status.value
        val raw = runCatching { bodyAsText() }.getOrDefault("")
        val envelope = runCatching { ProtocolJson.decodeFromString<ErrorResponse>(raw) }.getOrNull()

        if (envelope == null) {
            // A proxy or gateway can answer with HTML long before the application sees the
            // request; treating that as a contract error would produce nonsense.
            return ApiError.Server(status, null, "Unexpected response ($status)")
        }

        val error = ApiError.fromCode(
            status = status,
            code = envelope.error.code,
            message = envelope.error.message,
            errors = envelope.error.validationErrors.orEmpty(),
        )

        // Prefer the server's own backoff over any guess of ours.
        return if (error is ApiError.RateLimited) {
            ApiError.RateLimited(
                retryAfterSeconds = headers["Retry-After"]?.toLongOrNull(),
                message = error.message.orEmpty(),
            )
        } else {
            error
        }
    }
}
